// src/shared/changelog/grouping.ts
import { ChangelogEntry } from "./types";

/**
 * A month's worth of entries. Derived from `releasedAt` — never authored, which
 * is the whole point of the flat model: nobody writes a month heading.
 */
export interface ChangelogMonthGroup {
  /** `YYYY-MM` — grouping identity and React key. */
  id: string;
  /** First day of the month, for display via `formatReleasePeriod`. */
  date: string;
  entries: ChangelogEntry[];
}

/**
 * Groups entries into months, newest month first and newest entry first inside
 * each month. Ties on `releasedAt` keep the source order — `Array.sort` is
 * stable — so the data file decides the order within a day.
 */
export function groupEntriesByMonth(
  entries: ChangelogEntry[]
): ChangelogMonthGroup[] {
  const byMonth = new Map<string, ChangelogEntry[]>();

  const newestFirst = [...entries].sort((a, b) =>
    b.releasedAt.localeCompare(a.releasedAt)
  );

  for (const entry of newestFirst) {
    const id = entry.releasedAt.slice(0, 7);
    const bucket = byMonth.get(id);
    if (bucket) {
      bucket.push(entry);
    } else {
      byMonth.set(id, [entry]);
    }
  }

  // Map preserves insertion order, and we inserted newest-first.
  return [...byMonth.entries()].map(([id, monthEntries]) => ({
    id,
    date: `${id}-01`,
    entries: monthEntries,
  }));
}
